/* Product-page module: gallery, variant picker, sticky ATC */
const MV = window.MV;

class MVGallery extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.stage = this.querySelector("[data-gallery-stage]");
    this.thumbs = [...this.querySelectorAll("[data-thumb]")];
    this.slides = [...this.querySelectorAll(".mv-pdp__slide")];
    // tap to zoom (mobile) / keyboard zoom (Enter, Space) — bind for every product, including
    // single-image ones (each slide carries tabindex/role/aria-label, so zoom must always work)
    this.slides.forEach((s) => {
      s.addEventListener("click", () => s.classList.toggle("is-zoom"));
      s.addEventListener("keydown", (e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        s.classList.toggle("is-zoom");
      });
    });
    // thumbnail navigation + scroll-sync only apply to multi-image galleries
    if (!this.thumbs.length) return;
    this.thumbs.forEach((t) => t.addEventListener("click", () => this.goto(t.dataset.thumb)));
    this.stage.addEventListener("scroll", MV.debounce(() => this.sync(), 90), { passive: true });
    this.sync();
  }
  goto(id) {
    const slide = this.slides.find((s) => s.dataset.mediaId === id);
    if (slide) slide.scrollIntoView({ behavior: MV.reducedMotion ? "auto" : "smooth", inline: "start", block: "nearest" });
  }
  sync() {
    const i = Math.round(this.stage.scrollLeft / this.stage.clientWidth);
    const id = this.slides[i]?.dataset.mediaId;
    this.thumbs.forEach((t) => t.setAttribute("aria-current", t.dataset.thumb === id ? "true" : "false"));
  }
  disconnectedCallback() {}
}
customElements.define("mv-gallery", MVGallery);

class MVVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.variants = JSON.parse(this.querySelector("[data-variants]").textContent);
    this.form = this.querySelector("#ProductForm");
    this.idInput = this.querySelector("[data-variant-id]");
    this.idInput.tabIndex = -1;
    this.idInput.setAttribute("aria-hidden", "true");
    this.atc = this.querySelector("[data-atc]");
    this.atcText = this.querySelector("[data-atc-text]");
    this.buyNow = this.querySelector("[data-buynow]");
    this.priceEl = this.querySelector("[data-price]");
    this.scarcity = this.querySelector("[data-scarcity]");

    this.addEventListener("change", (e) => { if (e.target.matches('input[type="radio"]')) this.onChange(); });
    this.form.addEventListener("submit", (e) => { e.preventDefault(); this.add(false); });
    this.querySelector("[data-buynow]")?.addEventListener("click", () => this.add(true));
    this.onChange(true);
  }
  selectedOptions() {
    return [...this.querySelectorAll('input[type="radio"]:checked')].map((r) => r.value);
  }
  match() {
    const chosen = this.selectedOptions();
    if (!chosen.length) return this.variants[0];
    return this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
  }
  onChange(initial) {
    const v = this.match();
    if (!v) { this.setUnavailable(); return; }
    this.current = v;
    this.idInput.value = v.id;
    this.priceEl.textContent = MV.money(v.price);
    const cmp = this.querySelector("[data-compare]");
    const off = this.querySelector("[data-off]");
    if (v.compare_at_price && v.compare_at_price > v.price) {
      if (cmp) cmp.textContent = MV.money(v.compare_at_price);
      if (off) off.textContent = Math.round((1 - v.price / v.compare_at_price) * 100) + "% OFF";
    } else { if (cmp) cmp.textContent = ""; if (off) off.textContent = ""; }
    // availability
    this.atc.disabled = !v.available;
    this.atcText.textContent = v.available ? "Add to cart" : "Sold out";
    if (this.buyNow) this.buyNow.disabled = !v.available;
    // scarcity (real inventory only)
    if (v.available && v.inventory_management && typeof v.inventory_quantity === "number" && v.inventory_quantity > 0 && v.inventory_quantity <= 5) {
      this.scarcity.textContent = `Only ${v.inventory_quantity} left`;
      this.scarcity.hidden = false;
    } else this.scarcity.hidden = true;
    // reflect crossed-out unavailable options for the current partial selection
    this.markUnavailableSwatches();
    if (!initial) {
      const url = new URL(location.href);
      url.searchParams.set("variant", v.id);
      history.replaceState({}, "", url);
    }
  }
  markUnavailableSwatches() {
    // for each option group, cross out values that have no available variant given the other selected options
    const groups = [...this.querySelectorAll(".mv-pdp__option")];
    const chosen = this.selectedOptions();
    groups.forEach((group, gi) => {
      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const trial = [...chosen]; trial[gi] = radio.value;
        const anyAvail = this.variants.some((v) => v.available && v.options.every((o, i) => trial[i] == null || o === trial[i]));
        radio.closest(".mv-pdp__swatch").classList.toggle("is-unavailable", !anyAvail);
      });
    });
  }
  setUnavailable() {
    this.atc.disabled = true;
    this.atcText.textContent = "Unavailable";
    if (this.buyNow) this.buyNow.disabled = true;
    this.scarcity.hidden = true;
  }
  async add(buyNow) {
    if (this.busy) return;
    this.busy = true;
    if (!this.current?.available) { this.busy = false; return; }
    this.atc.disabled = true;
    if (this.buyNow) this.buyNow.disabled = true;
    const prev = this.atcText.textContent;
    this.atcText.textContent = "Adding…";
    try {
      const res = await fetch(`${MV.root()}cart/add.js`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: this.current.id, quantity: 1 }),
      });
      if (!res.ok) throw new Error(res.status);
      const cart = await (await fetch(`${MV.root()}cart.js`)).json();
      MV.emit("cart:updated", { cart });
      if (buyNow) { window.location.href = `${MV.root()}checkout`; return; }
      MV.emit("cart:added", { cart });
      this.atcText.textContent = prev; this.atc.disabled = false;
      if (this.buyNow) this.buyNow.disabled = false;
      this.busy = false;
    } catch {
      this.atcText.textContent = "Couldn't add — retry"; this.atc.disabled = false;
      if (this.buyNow) this.buyNow.disabled = false;
      this.busy = false;
    }
  }
  disconnectedCallback() {}
}
customElements.define("mv-variant-picker", MVVariantPicker);

class MVStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.picker = document.querySelector("mv-variant-picker");
    this.priceEl = this.querySelector("[data-sticky-price]");
    this.btn = this.querySelector("[data-sticky-atc]");
    this.mainAtc = document.querySelector("[data-atc]");
    this.btn.addEventListener("click", () => this.picker?.add(false));
    // mirror price + availability
    this.mo = new MutationObserver(() => this.sync());
    const price = document.querySelector("[data-price]");
    if (price) this.mo.observe(price, { childList: true, characterData: true, subtree: true });
    this.onPickerChange = () => this.sync();
    this.picker?.addEventListener("change", this.onPickerChange);
    // show when main ATC scrolled out of view
    // (observe the ATC button row itself, not the whole buybox — the buybox also
    // contains the delivery banner + accordions, which on some products are tall
    // enough that its bottom edge never clears the viewport, so the sticky bar
    // would never appear)
    const box = document.querySelector(".mv-pdp__atc-row") || document.querySelector("#MvBuyBox");
    if (box) {
      this.io = new IntersectionObserver(([e]) => { this.hidden = e.isIntersecting || e.boundingClientRect.top > 0; }, { rootMargin: "-80px 0px 0px 0px" });
      this.io.observe(box);
    }
    this.sync();
  }
  sync() {
    const price = document.querySelector("[data-price]");
    if (price && this.priceEl) this.priceEl.textContent = price.textContent;
    if (this.mainAtc && this.btn) {
      this.btn.disabled = this.mainAtc.disabled;
      this.btn.textContent = this.mainAtc.disabled ? "Sold out" : "Add to cart";
    }
  }
  disconnectedCallback() { this.mo?.disconnect(); this.io?.disconnect(); this.picker?.removeEventListener("change", this.onPickerChange); }
}
customElements.define("mv-sticky-atc", MVStickyAtc);

// size guide open/close (delegated, module-global)
document.addEventListener("click", (e) => {
  if (e.target.closest("[data-open-sizeguide]")) {
    const d = document.getElementById("MvSizeGuide");
    if (d && !d.open) { d.showModal(); MV.lockScroll(); }
  }
  const sg = document.getElementById("MvSizeGuide");
  if (sg?.open && (e.target === sg || e.target.closest("[data-sizeguide-close]"))) { sg.close(); MV.unlockScroll(); }
  // size guide cm/in unit toggle
  const unitBtn = e.target.closest("[data-sg-unit]");
  if (unitBtn) {
    const wrap = unitBtn.closest("[data-size-guide]");
    if (wrap) {
      const inch = unitBtn.getAttribute("data-sg-unit") === "in";
      wrap.classList.toggle("is-inch", inch);
      wrap.querySelectorAll("[data-sg-unit]").forEach((b) => {
        const on = b === unitBtn;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }
  }
});
// ESC/native cancel dismissal also needs to unlock scroll (cancel doesn't bubble, so bind directly)
document.getElementById("MvSizeGuide")?.addEventListener("cancel", (e) => {
  e.preventDefault();
  e.target.close();
  MV.unlockScroll();
});

(function recentlyViewed() {
  const esc = MV.escapeHtml;
  const KEY = "mv:recent";
  const picker = document.querySelector("mv-variant-picker");
  const handle = picker?.dataset.url?.split("/products/")[1]?.split("?")[0];
  let list = [];
  try { list = JSON.parse(localStorage.getItem(KEY)) || []; } catch {}
  const others = list.filter((h) => h && h !== handle);
  // render others (before adding current)
  const wrap = document.getElementById("MvRecent");
  const track = document.getElementById("MvRecentTrack");
  if (wrap && track && others.length) {
    Promise.all(others.slice(0, 10).map((h) =>
      fetch(`${MV.root()}products/${encodeURIComponent(h)}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    )).then((items) => {
      const good = items.filter(Boolean);
      if (!good.length) return;
      track.innerHTML = good.map((p) => `
        <li class="mv-prow__item"><div class="mv-card">
          <div class="mv-card__visual"><a href="${esc(p.url)}" class="mv-card__media">
            ${p.featured_image ? `<img class="mv-card__img" src="${esc(p.featured_image)}${p.featured_image.includes("?") ? "&" : "?"}width=400" alt="" loading="lazy">` : ""}
          </a></div>
          <div class="mv-card__info"><h3 class="mv-card__title"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
          <div class="mv-card__price"><span class="mv-card__price-now">${MV.money(p.price)}</span></div></div>
        </div></li>`).join("");
      wrap.hidden = false;
    });
  }
  // add current to front
  if (handle) {
    const next = [handle, ...list.filter((h) => h !== handle)].slice(0, 12);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch {}
  }
})();
